import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './UserBookmarksSettings.translations.json';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import React, {
  BaseSyntheticEvent,
  FC,
  RefObject,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import { colorBgContainer, colorGray } from '@atlas-frontend/design-tokens';
import {
  ErrorAlertWithDrawer,
  InfoIcon,
  InfoIconUrl,
  Padding,
  Spinner,
  Stack,
  useNavigationBlockers,
} from '../../../common/components';
import { useListUserBookmarkQuery } from './query/useGetUserBookmarkQuery';
import {
  UserBookmarkQueryDtoType,
  UserBookmarksFormDataType,
} from './schema/types';
import { Flex } from 'antd';
import {
  DeleteTableRowButton,
  Form,
  FormRef,
  TableOperationButton,
  TableRowVerticalPositionChangeHandler,
  TanstackFormTable,
} from '../../../common/form';
import { useMutationErrors } from '../../../common/queryClient';
import { useMutateUserBookmarkQuery } from './query/useMutateUserBookmarkQuery';
import { GetApiErrorMessage } from '../../../common/utils/ValidationMessagesMap';
import * as Icon from 'react-feather';
import { UserBookmarkModal } from './UserBookmarkModal';
import { DragVerticalTableRowButton } from '../../../common/form/table/tanstack/DragVerticalTableRowButton';
import { UserBookmarksValidFormDataSchema } from './schema/UserBookmarkSchema';
import { useMutateUserBookmarksQuery } from './query/useMutateUserBookmarksQuery';
import { useMoveFormTableRow } from '../../../common/form/table/tanstack/useMoveFormTableRow';
import { FieldValues, useFormContext } from 'react-hook-form';
import { useFormSubmitHandler } from '../../../common/form/AdditionalFormContext';

const { addTranslations, t } = i18nNsFactory('user-bookmarks');
addTranslations(translations);

const translateMutationError: GetApiErrorMessage = (code, meta) =>
  t('alert.error.code.' + code, meta?.args && { ...meta.args });

const columnHelper = createColumnHelper<UserBookmarkQueryDtoType>();
function getColumns(
  onPositionChange?: TableRowVerticalPositionChangeHandler,
  onEdit?: (bookmark: UserBookmarkQueryDtoType) => void,
  onDelete?: (bookmark: UserBookmarkQueryDtoType) => void,
): ColumnDef<UserBookmarkQueryDtoType, any>[] {
  const columns: ColumnDef<UserBookmarkQueryDtoType, any>[] = [];
  columns.push(
    columnHelper.accessor('sort' as any, {
      header: '',
      cell: (props) => (
        <DragVerticalTableRowButton
          {...props}
          onChangePosition={onPositionChange}
        />
      ),
      enableSorting: false,
      enableColumnFilter: false,
      meta: {
        width: 48,
      },
    }),
    columnHelper.accessor('name', {}),
    columnHelper.accessor('operations' as any, {
      enableSorting: false,
      enableColumnFilter: false,
      cell: (props) => (
        <Flex justify="center" gap="small">
          <TableOperationButton
            id={`edit-table-row-${props.row.index}`}
            title={t('button.edit')}
            icon={<Icon.Edit3 size={18} />}
            onClick={() => onEdit?.(props.row.original)}
          />
          <DeleteTableRowButton
            id={`delete-table-row-${props.row.index}`}
            confirm={{
              onConfirm: () => onDelete?.(props.row.original),
            }}
          />
        </Flex>
      ),
      meta: { width: 80 },
    }) as ColumnDef<UserBookmarkQueryDtoType, any>,
  );
  return columns;
}

export const UserBookmarksSettings = () => {
  const [editBookmark, setEditBookmark] = useState<
    UserBookmarkQueryDtoType | undefined
  >(undefined);
  const ref: RefObject<FormRef<any> | null> = useRef(null);

  const handleEdit = useCallback((bookmark: UserBookmarkQueryDtoType) => {
    setEditBookmark(bookmark);
  }, []);
  const handleClose = useCallback(() => {
    setEditBookmark(undefined);
  }, []);

  const {
    data: bookmarks = [],
    isLoading,
    refetch,
  } = useListUserBookmarkQuery();

  const { clear: clearBlockers } = useNavigationBlockers();
  const { mutateAsync: mutateUserBookmarks } = useMutateUserBookmarksQuery();
  const { mutationErrors, setMutationErrors, clearMutationErrors } =
    useMutationErrors(translateMutationError);

  const handleSuccess = useCallback(async () => {
    clearBlockers();
    await refetch().then((res: any) =>
      ref.current?.form.setValue('bookmarks', res.data),
    );
    handleClose();
  }, [clearBlockers, handleClose, refetch]);

  const handleSubmit = useCallback(
    (validFormData: UserBookmarksFormDataType) => {
      clearMutationErrors();
      return mutateUserBookmarks({
        data: validFormData.bookmarks,
      })
        .then(() => {
          clearBlockers();
        })
        .catch((apiErrorResponse) => {
          setMutationErrors(apiErrorResponse.response?.data.errors || []);
        });
    },
    [
      clearBlockers,
      clearMutationErrors,
      mutateUserBookmarks,
      setMutationErrors,
    ],
  );

  if (isLoading) {
    return <Spinner />;
  }

  if (!bookmarks) {
    return null;
  }

  return (
    <ContentWrapper size="md">
      <StyledHeader vertical={12} horizontal>
        <Flex gap="small" align="center">
          <h4>{t('title')}</h4>
          <InfoIcon infoIconUrl={InfoIconUrl.USER_BOOKMARKS_SETTINGS} />
        </Flex>
        {mutationErrors && (
          <ErrorAlertWithDrawer
            errorAction={t('action')}
            validationMessagesMap={mutationErrors}
          />
        )}
      </StyledHeader>
      <Form
        schema={UserBookmarksValidFormDataSchema}
        defaultValues={{ bookmarks: bookmarks }}
        serverErrors={mutationErrors}
        onSubmit={handleSubmit}
        formRef={ref}
      >
        <UserBookmarksSettingsInner
          onSuccess={handleSuccess}
          onEdit={handleEdit}
        />
      </Form>
      {editBookmark && (
        <UserBookmarkModal
          mode="edit"
          bookmark={editBookmark}
          onClose={handleClose}
          onSuccess={handleSuccess}
        />
      )}
    </ContentWrapper>
  );
};

export const UserBookmarksSettingsInner: FC<{
  onSuccess: () => void;
  onEdit: (bookmark: UserBookmarkQueryDtoType) => void;
}> = (props) => {
  const form = useFormContext();
  const formSubmitHandler = useFormSubmitHandler();

  const handleSubmit = useCallback(
    (data: FieldValues, event?: BaseSyntheticEvent) =>
      formSubmitHandler(data, false, event),
    [formSubmitHandler],
  );

  const submit = useMemo(() => {
    return form.handleSubmit(handleSubmit, (errors, event) => {
      const values = form.getValues();
      if (import.meta.env.DEV) {
        console.error('Form validation failed', { errors, values, event });
      }
    });
  }, [form, handleSubmit]);

  const { mutateAsync: mutateUserBookmark } = useMutateUserBookmarkQuery();
  const { setMutationErrors, clearMutationErrors } = useMutationErrors(
    translateMutationError,
  );

  const move = useMoveFormTableRow(form);
  const handlePositionChange = useCallback(
    (fromIndex: number, toIndex: number) => {
      // 1. move row data object in data array
      move('bookmarks', fromIndex, toIndex);
      void submit();
    },
    [move, submit],
  );

  const handleDelete = useCallback(
    (bookmark: UserBookmarkQueryDtoType) => {
      if (bookmark.id) {
        clearMutationErrors();
        return mutateUserBookmark({ id: bookmark.id })
          .then(() => {
            props.onSuccess();
          })
          .catch((error) => {
            setMutationErrors(error?.response?.data?.errors ?? []);
          });
      }
    },
    [clearMutationErrors, mutateUserBookmark, props, setMutationErrors],
  );

  const columns: ColumnDef<UserBookmarkQueryDtoType, any>[] = useMemo(
    () => getColumns(handlePositionChange, props.onEdit, handleDelete),
    [handlePositionChange, props.onEdit, handleDelete],
  );

  return (
    <PaddingWrapper bottom horizontal>
      <TableWrapper id="user-bookmarks-table-wrapper">
        <TanstackFormTable
          name="bookmarks"
          columns={columns}
          hideClearFilters
          hideHeader
          virtual
          scrollContainerSelector="#user-bookmarks-table-wrapper"
        />
      </TableWrapper>
    </PaddingWrapper>
  );
};

const StyledHeader = styled(Padding)`
  border-bottom: 1px solid ${colorGray[2]};
`;

const ContentWrapper = styled(Stack)`
  background-color: ${colorBgContainer};
  border: 1px solid ${colorGray[2]};
  min-height: 200px;
  max-height: 2000px;
`;

const PaddingWrapper = styled(Padding)`
  overflow: auto;
`;

const TableWrapper = styled.div`
  display: flex;
  flex-flow: row wrap;
`;
